// WhyICard — rotating "why I quit" anchor on Today (PR 1A, Gap 1).
//
// Reads /api/me/whys. When the user has 1+ active whys, rotates through
// them every 8s (paused if prefers-reduced-motion). Tap → opens an edit
// sheet for the currently-showing why. When the user has 0 active whys,
// shows an empty-state CTA that deep-links to Settings → Whys manager.
//
// Mobile discipline:
//  - Card sits in Today's Zone A, below TodaysNote, above the savings strip.
//  - Touch target is the whole card (≥44px tall via Card padding) with
//    a visible "Edit" link in the corner so the affordance is obvious.
//  - Auto-rotate uses a CSS-driven fade so reduced-motion users see a
//    static snapshot of the first active why.
//  - Dots indicator below the body shows which why is visible.

import { useEffect, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Icon } from '../../components/ui/Icon';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Textarea';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { useApi } from '../../hooks/useApi';
import { apiPut, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { WHY_CATEGORIES, type WhyCategory, type WhyRow, type WhysResponse } from '@shared/types';

const ROTATE_MS = 8000;

const CATEGORY_LABELS: Record<WhyCategory, string> = {
  money: 'Money',
  identity: 'Identity',
  kids: 'Kids',
  health: 'Health',
  hiding: 'Hiding',
  'time-loss': 'Time',
  other: 'Other',
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function WhyICard() {
  const res = useApi<WhysResponse>('/api/me/whys');
  const toast = useToast();
  const [index, setIndex] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [editCategory, setEditCategory] = useState<WhyCategory>('other');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const whys = useMemo(() => (res.status === 'ok' ? res.data.whys : []), [res]);
  const active = useMemo(() => whys.filter((w) => w.isActive), [whys]);

  // Auto-rotate. Reduced-motion users get the first why and no rotation.
  useEffect(() => {
    if (active.length <= 1) return;
    if (prefersReducedMotion()) return;
    const handle = window.setInterval(() => {
      setIndex((i) => (i + 1) % active.length);
    }, ROTATE_MS);
    return () => window.clearInterval(handle);
  }, [active.length]);

  // Clamp index when the list shrinks (e.g. user archived one in Settings).
  useEffect(() => {
    if (active.length === 0) {
      setIndex(0);
    } else if (index >= active.length) {
      setIndex(0);
    }
  }, [active.length, index]);

  // Loading / error / empty.
  if (res.status === 'loading' || res.status === 'idle') return null;
  if (res.status === 'error') return null;

  if (active.length === 0) {
    return (
      <Card className="why-card why-card-empty">
        <p className="why-card-body why-card-empty-prompt">
          Add the reasons you're quitting — they'll surface here when you need them.
        </p>
        <a className="why-card-cta" href="/app/more#whys">
          Add my reasons →
        </a>
      </Card>
    );
  }

  const current: WhyRow = active[index];

  function openEdit() {
    setEditingId(current.id);
    setEditBody(current.body);
    setEditCategory(current.category);
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editingId) return;
    const trimmed = editBody.trim();
    if (trimmed.length === 0) return;
    setSaving(true);
    try {
      await apiPut(`/api/me/whys/${editingId}`, {
        body: trimmed.slice(0, 240),
        category: editCategory,
      });
      setEditOpen(false);
      toast.push('Why updated');
      res.refresh();
    } catch (err) {
      const msg =
        err instanceof ApiClientError && err.code === 'invalid_input'
          ? 'Keep it under 240 characters.'
          : 'Could not save — try again.';
      toast.push(msg, { tone: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Card
        className="why-card"
        accentLeft
        onClick={openEdit}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openEdit();
          }
        }}
        aria-label={`Why I quit, ${index + 1} of ${active.length}. Tap to edit.`}
      >
        <div className="why-card-header">
          <span className="why-card-label">Why I quit</span>
          <span className="why-card-edit-affordance" aria-hidden="true">
            <Icon icon={Pencil} size={14} />
          </span>
        </div>
        <p className="why-card-body" key={current.id /* re-trigger fade-in */}>
          {current.body}
        </p>
        {active.length > 1 ? (
          <div className="why-card-dots" role="tablist" aria-label="rotating reasons">
            {active.map((w, i) => (
              <span
                key={w.id}
                className={`why-card-dot${i === index ? ' why-card-dot-active' : ''}`}
                aria-hidden="true"
              />
            ))}
          </div>
        ) : null}
      </Card>

      <BottomSheet
        open={editOpen}
        onOpenChange={(o) => {
          if (!saving) setEditOpen(o);
        }}
        title="Edit this why"
      >
        <div
          className="stack"
          style={{ '--stack-gap': 'var(--space-4)' } as React.CSSProperties}
        >
          <div className="field">
            <span className="field-label">Category</span>
            <SegmentedControl<WhyCategory>
              value={editCategory}
              onChange={setEditCategory}
              ariaLabel="why category"
              options={WHY_CATEGORIES.slice(0, 4).map((c) => ({
                value: c,
                label: CATEGORY_LABELS[c],
              }))}
            />
            <div style={{ marginBlockStart: 'var(--space-2)' }}>
              <SegmentedControl<WhyCategory>
                value={editCategory}
                onChange={setEditCategory}
                ariaLabel="why category continued"
                options={WHY_CATEGORIES.slice(4).map((c) => ({
                  value: c,
                  label: CATEGORY_LABELS[c],
                }))}
              />
            </div>
          </div>
          <Textarea
            label="Your reason"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={3}
            maxLength={240}
            autoCapitalize="sentences"
            autoCorrect="on"
            placeholder="What's the truth you'll need when it's hard?"
          />
          <div className="text-caption muted">
            {editBody.length}/240
          </div>
          <div
            className="row modal-actions"
            style={{ justifyContent: 'flex-end', gap: 'var(--space-2)' }}
          >
            <Button
              variant="secondary"
              onClick={() => setEditOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void saveEdit()}
              loading={saving}
              disabled={editBody.trim().length === 0}
            >
              Save
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
